/**
 * BDL Momentos — "Brownie para todos os momentos" (home).
 *
 * Setas + bolinhas no mobile navegam 1 item por vez. O swipe também:
 * um gesto = um passo (igual ao botão), sem pular vários cards.
 * No desktop os controles ficam escondidos via CSS.
 */
class BdlMomentosCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-bdl-momentos-track]');
    this.prevBtn = this.querySelector('[data-bdl-momentos-prev]');
    this.nextBtn = this.querySelector('[data-bdl-momentos-next]');
    this.dots = Array.from(this.querySelectorAll('[data-bdl-momentos-dot]'));
    this.index = 0;
    this.isSnapping = false;
    this.swipeStartX = 0;
    this.swipeTracking = false;

    if (!this.track) return;

    this.prevBtn?.addEventListener('click', () => this.goBy(-1));
    this.nextBtn?.addEventListener('click', () => this.goBy(1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.snapToIndex(Number(dot.dataset.index)));
    });
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.bindOneStepSwipe(this.track);

    this.updateControls();
  }

  disconnectedCallback() {
    window.clearTimeout(this.scrollTimer);
    window.clearTimeout(this.snapTimer);
    this.unbindOneStepSwipe();
  }

  bindOneStepSwipe(el) {
    this._onSwipeDown = (event) => {
      if (event.pointerType === 'mouse') return;
      this.swipeTracking = true;
      this.swipeStartX = event.clientX;
    };
    this._onSwipeUp = (event) => {
      if (!this.swipeTracking) return;
      this.swipeTracking = false;
      if (event.pointerType === 'mouse') return;

      const dx = event.clientX - this.swipeStartX;
      const threshold = Math.min(56, Math.max(36, el.clientWidth * 0.12));
      if (dx <= -threshold) this.goBy(1);
      else if (dx >= threshold) this.goBy(-1);
      else this.snapToIndex(this.index);
    };
    this._onSwipeCancel = () => {
      this.swipeTracking = false;
    };

    el.addEventListener('pointerdown', this._onSwipeDown);
    el.addEventListener('pointerup', this._onSwipeUp);
    el.addEventListener('pointercancel', this._onSwipeCancel);
  }

  unbindOneStepSwipe() {
    if (!this.track) return;
    this.track.removeEventListener('pointerdown', this._onSwipeDown);
    this.track.removeEventListener('pointerup', this._onSwipeUp);
    this.track.removeEventListener('pointercancel', this._onSwipeCancel);
  }

  cards() {
    return Array.from(this.track.children);
  }

  maxIndex() {
    return Math.max(0, this.cards().length - 1);
  }

  goBy(direction) {
    this.snapToIndex(this.index + direction);
  }

  /**
   * @param {number} nextIndex
   * @param {ScrollBehavior} behavior
   */
  snapToIndex(nextIndex, behavior = 'smooth') {
    const cards = this.cards();
    if (!cards.length) return;

    this.index = Math.min(Math.max(0, nextIndex), this.maxIndex());
    const target = cards[this.index];

    this.isSnapping = true;
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior });
    this.updateControls();

    window.clearTimeout(this.snapTimer);
    this.snapTimer = window.setTimeout(() => {
      this.isSnapping = false;
    }, behavior === 'smooth' ? 400 : 50);
  }

  onScroll() {
    if (this.isSnapping) return;

    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      const cards = this.cards();
      if (!cards.length) return;

      const scrollLeft = this.track.scrollLeft;
      let closest = 0;
      let closestDist = Infinity;

      cards.forEach((card, i) => {
        const dist = Math.abs(card.offsetLeft - this.track.offsetLeft - scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });

      this.index = closest;
      this.updateControls();
    }, 90);
  }

  updateControls() {
    const max = this.maxIndex();
    if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.index >= max;

    this.dots.forEach((dot, i) => {
      const isActive = i === this.index;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('bdl-momentos-carousel')) {
  customElements.define('bdl-momentos-carousel', BdlMomentosCarousel);
}
